import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";

import { Label } from "../../../components/ui/label";

import { Edit2Icon } from "lucide-react";
import { Input } from "../../../components/ui/input";

export function EditIndexOptionsModal({
  clientId,
  broker,
  prevMultiplier,
  prevBankNiftyMultiplier,
  prevFinNiftyMultiplier,
  prevMidcpNiftyMultiplier,
  prevSensexMultiplier,
  prevMcxMultiplier,
  prevMultiAssetMultiplier,
  isMultiAsset,
  product,
  handleEditIndexOptionsConfig,
}) {
  const [open, setOpen] = useState(false);
  const [indexMultiplier, setIndexMultiplier] = useState(prevMultiplier);
  const [bankNiftyMultiplier, setBankNiftyMultiplier] = useState(prevBankNiftyMultiplier);
  const [finNiftyMultiplier, setFinNiftyMultiplier] = useState(prevFinNiftyMultiplier);
  const [midcpNiftyMultiplier, setMidcpNiftyMultiplier] = useState(prevMidcpNiftyMultiplier);
  const [sensexMultiplier, setSensexMultiplier] = useState(prevSensexMultiplier);
  const [mcxMultiplier, setMcxMultiplier] = useState(prevMcxMultiplier);
  const [multiAssetMultiplier, setMultiAssetMultiplier] = useState(prevMultiAssetMultiplier ?? 0);

  useEffect(() => {
    if (open) {
      setIndexMultiplier(prevMultiplier ?? 0);
      setBankNiftyMultiplier(prevBankNiftyMultiplier ?? 0);
      setFinNiftyMultiplier(prevFinNiftyMultiplier ?? 0);
      setMidcpNiftyMultiplier(prevMidcpNiftyMultiplier ?? 0);
      setSensexMultiplier(prevSensexMultiplier ?? 0);
      setMcxMultiplier(prevMcxMultiplier ?? 0);
      setMultiAssetMultiplier(prevMultiAssetMultiplier ?? 0);
    }
  }, [
    open,
    prevMultiplier,
    prevBankNiftyMultiplier,
    prevFinNiftyMultiplier,
    prevMidcpNiftyMultiplier,
    prevSensexMultiplier,
    prevMcxMultiplier,
    prevMultiAssetMultiplier,
  isMultiAsset,
  product,
  ]);

  const handleEditIndexMultiplier = () => {
    const payload = {
      clientId: clientId,
      broker: broker,
      indexMultiplier: Number(indexMultiplier),
      bankniftyMultiplier: Number(bankNiftyMultiplier),
      finniftyMultiplier: Number(finNiftyMultiplier),
      midcpniftyMultiplier: Number(midcpNiftyMultiplier),
      sensexMultiplier: Number(sensexMultiplier),
      mcxMultiplier: Number(mcxMultiplier),
      multiAssetMultiplier: Number(multiAssetMultiplier || 0),
      multi_asset_multiplier: Number(multiAssetMultiplier || 0),
    };

    handleEditIndexOptionsConfig(clientId, payload);

    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-zinc-800 text-white px-2.5 hover:bg-zinc-700  rounded-[5px]">
          <Edit2Icon className="w-4 h-4 " />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px] border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-white">
            Edit Index Options Multiplier
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="col-span-2 p-3 bg-purple-950/30 border border-purple-800/40 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-purple-200 font-semibold text-sm">
                Multi-Asset Multiplier
              </Label>
              {isMultiAsset && Number(multiAssetMultiplier) > 0 && (
                <span className="text-[11px] font-semibold bg-purple-900/80 text-purple-200 px-2 py-0.5 rounded border border-purple-500/40">
                  ACTIVE (Overrides Nifty, Midcap, BNF, Sensex)
                </span>
              )}
            </div>
            <Input
              type="number"
              placeholder="Enter Multi-Asset Multiplier (0 to disable)"
              className="text-white bg-zinc-900 border-zinc-700 focus:border-purple-500"
              value={multiAssetMultiplier}
              onChange={(e) => setMultiAssetMultiplier(e.target.value)}
            />
            {product && product !== 'multi_asset' && product !== 'multiasset' && (
              <p className="text-[11px] text-amber-400/90 font-medium">
                Note: Client product is {String(product).toUpperCase()}. Multi-Asset multiplier override only applies when client product is Multi-Asset.
              </p>
            )}
            <p className="text-[11px] text-zinc-400">
              When &gt; 0, this multiplier is applied across Nifty, Bank Nifty, Fin Nifty, Midcap, and Sensex for Multi-Asset clients. MCX remains independent. Set to 0 to use individual multipliers below.
            </p>
          </div>
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Index Multiplier</Label>
            <Input
              type="number"
              placeholder="Enter Multiplier"
              className="text-white"
              value={indexMultiplier}
              onChange={(e) => setIndexMultiplier(e.target.value)}
            />
          </div>
          
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Bank Nifty Multiplier</Label>
            <Input
              type="number"
              placeholder="Enter Bank Nifty Multiplier"
              className="text-white"
              value={bankNiftyMultiplier}
              onChange={(e) => setBankNiftyMultiplier(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Fin Nifty Multiplier</Label>
            <Input
              type="number"
              placeholder="Enter Fin Nifty Multiplier"
              className="text-white"
              value={finNiftyMultiplier}
              onChange={(e) => setFinNiftyMultiplier(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Midcap Nifty Multiplier</Label>
            <Input
              type="number"
              placeholder="Enter Midcap Nifty Multiplier"
              className="text-white"
              value={midcpNiftyMultiplier}
              onChange={(e) => setMidcpNiftyMultiplier(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Sensex Multiplier</Label>
            <Input
              type="number"
              placeholder="Enter Sensex Multiplier"
              className="text-white"
              value={sensexMultiplier}
              onChange={(e) => setSensexMultiplier(e.target.value)}
            />
          </div>
          
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">MCX Multiplier</Label>
            <Input
              type="number"
              placeholder="Enter MCX Multiplier"
              className="text-white"
              value={mcxMultiplier}
              onChange={(e) => setMcxMultiplier(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="submit"
            className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700"
            onClick={handleEditIndexMultiplier}
          >
            Update Multiplier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}